import { Modality } from './modality';
import { Image3D } from '../patient/image3d';
import { PetSeriesMetadata } from '../patient/petSuv';
import { formatDicomTime } from './utils';

export interface FormattedPetMetadata {
    weight: string;
    injectedDose: string;
    halfLife: string;
    injectionTime: string;
    scanTime: string;
}

/**
 * Unit suffixes for image intensity values, per modality.
 */
export enum ImageUnitSuffix {
    HU = 'HU',
    SUVbw = 'SUVbw [g/ml]',
    kBqPerMl = 'kBq/ml',
    MBqPerMl = 'MBq/ml',
    Gy = 'Gy',
}

/**
 * Formats any image cursor value as a display string with the appropriate unit.
 *
 * - CT  → `"123 HU"`
 * - PET → `"5.2 SUVbw [g/ml]"` (SUV) or `"45.3 kBq/ml"` (fallback)
 * - Other → `"123"`
 *
 * @param value - Raw pixel intensity value from the image volume.
 * @param modality - The DICOM modality of the image.
 * @param image - Optional Image3D instance, used to determine SUV availability for PET.
 */
export function formatValueWithUnit(
    value: number,
    modality: Modality | undefined,
    image?: Image3D
): string {
    switch (modality) {
        case Modality.CT:
            return `${value.toFixed(0)} ${ImageUnitSuffix.HU}`;
        case Modality.PT: {
            const suvFactor = image?.suvFactor ?? null;
            return formatPetDisplayValue(value, suvFactor);
        }
        default:
            // MR and other unitless modalities carry arbitrary intensity
            // units, so sub-integer precision is not clinically meaningful.
            // Show whole numbers only, matching the CT (HU) display.
            return `${value.toFixed(0)}`;
    }
}

/**
 * Formats a dose value for display: e.g. `"12.3 Gy"`.
 *
 * @param value - Dose intensity value in Gy.
 */
export function formatDoseDisplayValue(value: number): string {
    return `${value.toFixed(1)} ${ImageUnitSuffix.Gy}`;
}

/**
 * Returns the appropriate unit suffix for a given modality and image.
 * For PET images, returns 'SUVbw [g/ml]' if SUV factor is available, otherwise 'kBq/ml'.
 *
 * @param modality - The DICOM modality of the image.
 * @param image - Optional Image3D instance, used to determine SUV availability for PET.
 * @returns The unit suffix string, or empty string if no unit applies.
 */
export function getUnitSuffix(
    modality: Modality | undefined,
    image?: Image3D
): string {
    switch (modality) {
        case Modality.CT:
            return ` ${ImageUnitSuffix.HU}`;
        case Modality.PT:
            return image?.suvFactor != null
                ? ` ${ImageUnitSuffix.SUVbw}`
                : ` ${ImageUnitSuffix.kBqPerMl}`;
        default:
            return '';
    }
}

/**
 * Returns the compact Bq/ml unit label appropriate for a given raw value magnitude.
 */
function getCompactBqUnit(rawValue: number): ImageUnitSuffix {
    const abs = Math.abs(rawValue);
    if (abs >= 1e6) {
        return ImageUnitSuffix.MBqPerMl;
    }
    return ImageUnitSuffix.kBqPerMl;
}

/**
 * Returns the PET unit label, using compact Bq notation when SUV is unavailable.
 *
 * @param suvFactor - SUV conversion factor, or null if unavailable.
 * @param referenceValue - A representative raw pixel value used to pick the
 *   correct Bq prefix (e.g. max of WW and WC). Only used when suvFactor is null.
 */
export function getPetUnitLabel(
    suvFactor: number | null,
    referenceValue: number = 0
): string {
    if (suvFactor !== null) {
        return ImageUnitSuffix.SUVbw;
    }
    return getCompactBqUnit(referenceValue);
}

/**
 * Formats a raw PET pixel value as a numeric string (no unit appended),
 * applying SUV conversion or compact Bq scaling as appropriate.
 *
 * @param rawValue - Raw pixel intensity value.
 * @param suvFactor - SUV conversion factor, or null if unavailable.
 */
export function formatPetNumericValue(
    rawValue: number,
    suvFactor: number | null
): string {
    if (suvFactor !== null) {
        return (rawValue * suvFactor).toFixed(1);
    }
    if (Math.abs(rawValue) >= 1e6) {
        return (rawValue / 1e6).toFixed(1);
    }
    return (rawValue / 1e3).toFixed(1);
}

/**
 * Formats a raw PET pixel value as a display string with unit appended.
 *
 * @param rawValue - Raw pixel intensity value.
 * @param suvFactor - SUV conversion factor, or null if unavailable.
 */
export function formatPetDisplayValue(
    rawValue: number,
    suvFactor: number | null
): string {
    return `${formatPetNumericValue(rawValue, suvFactor)} ${getPetUnitLabel(suvFactor, rawValue)}`;
}

/**
 * Formats all PET series metadata fields used for SUVbw conversion into
 * human-readable strings with units.
 *
 * @param metadata - PET series metadata from DICOM headers.
 * @returns An object with each field formatted for display.
 */
export function formatPetMetadataValues(
    metadata: PetSeriesMetadata
): FormattedPetMetadata {
    const FALLBACK = '—';
    return {
        weight: metadata.patientWeightKg
            ? `${metadata.patientWeightKg.toFixed(1)} kg`
            : FALLBACK,
        injectedDose: metadata.radionuclideTotalDose
            ? `${(metadata.radionuclideTotalDose / 1e6).toFixed(1)} MBq`
            : FALLBACK,
        halfLife: metadata.radionuclideHalfLife
            ? `${metadata.radionuclideHalfLife.toFixed(1)} s`
            : FALLBACK,
        injectionTime: metadata.injectionDateTime
            ? `${formatDicomTime(metadata.injectionDateTime)} (hh:mm:ss)`
            : FALLBACK,
        scanTime: metadata.scanDateTime
            ? `${formatDicomTime(metadata.scanDateTime)} (hh:mm:ss)`
            : FALLBACK,
    };
}
